import { useCallback, useMemo } from 'react';

// store
import { storeGet, storeSet, useStoreValue } from '../store';

// helpers
import {
    EMPTY_SELECTION,
    getSelectedIds,
    nextSelection,
} from '../helpers/selection';

/**
 * Reads and writes the cross-panel selection for one kind of item.
 *
 * Every list that can be selected from — the cue sheet, the timeline, the
 * sub-pages of the My Studio left panel — goes through this hook, so ctrl/cmd
 * and shift behave the same everywhere and the Comm Center only ever has one
 * place to look.
 *
 * @param {string} kind        'cue' | 'video' | 'rec'
 * @param {array}  orderedIds  ids in display order, needed for a shift range
 */
const useSelection = (kind, orderedIds = []) => {
    const selection = useStoreValue('selection');

    const selectedIds = useMemo(() => getSelectedIds(selection, kind), [selection, kind]);

    const select = useCallback((id, event) => {
        // The store is read at call time rather than closed over: a list rendered
        // from a memoised row would otherwise toggle against a stale selection.
        storeSet('selection', nextSelection(storeGet('selection'), { kind, id, orderedIds, event }));
    }, [kind, orderedIds]);

    const selectOnly = useCallback((ids) => {
        const next = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);

        storeSet('selection', next.length
            ? { kind, ids: next, anchor: next[next.length - 1] }
            : EMPTY_SELECTION);
    }, [kind]);

    const clear = useCallback(() => storeSet('selection', EMPTY_SELECTION), []);

    const isSelected = useCallback((id) => selectedIds.includes(id), [selectedIds]);

    return { selection, selectedIds, isSelected, select, selectOnly, clear };
};

export default useSelection;
